import { useCallback, useEffect, useMemo, useState } from "react";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";
import { useFileContext } from "../../state/FileContext";
import { useFavoritesContext } from "../../state/FavoritesContext";
import { useSiteContext } from "../../state/SiteContext";
import { getFavoritesKey, getFavorites, setFavorites } from "../../state/favorites-storage";

interface RemoveFavoriteProps {
  label?: string;
  children: React.ReactNode;
}

/**
 * Button that removes the current file (from state) from favorites.
 * Disabled when the file is not in favorites.
 * Label from `label` prop or arg-label child (TextExtractor).
 */
export function RemoveFavorite({ label: labelProp, children }: RemoveFavoriteProps): React.ReactElement {
  const { fileState } = useFileContext();
  const { siteState } = useSiteContext();
  const { favoritesVersion, invalidateFavorites } = useFavoritesContext();

  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState("");

  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    const path = fileState.path;
    const siteName = siteState.site?.name;
    if (!path || !siteName) {
      setDisabled(true);
      return;
    }
    const key = getFavoritesKey(siteName);
    const favorites = getFavorites(key);
    const inFavorites = favorites.some((item) => item.value === path);
    setDisabled(!inFavorites);
  }, [fileState.path, siteState.site?.name, favoritesVersion]);

  const clickHandler = useCallback(() => {
    const path = fileState.path;
    const siteName = siteState.site?.name;
    if (!path || !siteName) return;
    const key = getFavoritesKey(siteName);
    const favorites = getFavorites(key);
    const next = favorites.filter((item) => item.value !== path);
    if (next.length === favorites.length) return;
    setFavorites(key, next);
    invalidateFavorites();
    setDisabled(true);
  }, [fileState.path, siteState.site?.name, invalidateFavorites]);

  const displayLabel = labelProp ?? labelValue;
  const button = useMemo(() => {
    if (disabled) {
      return <button disabled={true}>{displayLabel}</button>;
    }
    return <button onClick={clickHandler}>{displayLabel}</button>;
  }, [disabled, displayLabel, clickHandler]);

  return (
    <>
      {labelChild != null && (
        <TextExtractor onExtractedText={setLabelValue}>{labelChild.props.children}</TextExtractor>
      )}
      {button}
    </>
  );
}
